import _ from 'lodash';
import React, {FC, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {
  KeyboardActionsEnum,
  KeyboardMapping,
  KeyboardShortcut,
} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {isMacOS} from '../../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {intersperse} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {useKeyboardContext} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {
  keyRenderers,
  splitAndFixKeys,
} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcutFormatterHelpers';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';

interface InboxesTopbarTooltipProps {
  keyboardAction?: KeyboardActionsEnum;
  firstLine: ReactNode;
  secondLine: ReactNode;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-row-gap: 5px;
  padding: 5px 5px 4px 4px;
  overflow: hidden;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.white};
  display: flex;
  overflow: hidden;

  > span {
    flex-shrink: 1;
    ${ellipsis()};
  }
`;
const StyledSubtitleDiv = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.white};

  ul {
    padding: 0;
    padding-left: 12px;
    margin: 0;
    list-style-type: none;
    line-height: 18px;
  }

  ul li {
    position: relative;
  }

  ul li::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    border-radius: 4px;
    left: -10px;
    top: 9px;
    background: currentColor;
  }
`;

const StyledKeySpan = styled.span`
  transform: translateY(-1px);
  border-radius: 4px;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.white};
  background-color: ${(p) => p.theme.alphas.white30};
  text-transform: capitalize;
  height: 16px;
  min-width: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
`;

const StyledShortcutsWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 8px;
  margin-top: 1px;
  display: inline-grid;
  grid-auto-flow: column;
  grid-gap: 4px;
  grid-auto-columns: auto;
`;

/*
 * Component.
 */

export const InboxesTopbarTooltip: FC<InboxesTopbarTooltipProps> = (props) => {
  const {mapping} = useKeyboardContext();

  return (
    <StyledDiv>
      <StyledTitleDiv>
        <span>{props.firstLine}</span>{' '}
        {props.keyboardAction && renderKeyboardShortcuts(mapping, props.keyboardAction)}
      </StyledTitleDiv>
      <StyledSubtitleDiv>{props.secondLine}</StyledSubtitleDiv>
    </StyledDiv>
  );
};

/** Render the shortcuts for the specified action and mapping. */
function renderKeyboardShortcuts(mapping: KeyboardMapping, action: KeyboardActionsEnum) {
  const smallestShortcut = _(mapping[action])
    .reject((a) => a.sequence === 'del' && isMacOS())
    .sortBy((a) => a.sequence.length)
    .first();
  return (
    <StyledShortcutsWrapper>
      {smallestShortcut && renderKeyboardShortcutRow(smallestShortcut)}
    </StyledShortcutsWrapper>
  );
}

/** Render a single shortcut. */
function renderKeyboardShortcutRow(shortcut: KeyboardShortcut) {
  // Split into the sequences of keys that need to be pressed
  const shortcutKeys = shortcut.sequence.split(' ');

  // Render, separating each sequence with a space
  return <Fragment key={shortcut.sequence}>{shortcutKeys.map(renderKeyboardShortcutKeys)}</Fragment>;
}

/** Render a group of keys. */
function renderKeyboardShortcutKeys(shortcut: string) {
  const fixedKeys = splitAndFixKeys(shortcut);

  // Find the correct translation for each.
  const translatedKeys = fixedKeys.map((k) => {
    const renderer = keyRenderers[k];
    return (
      <StyledKeySpan key={k}>
        <span>{renderer ? renderer() : k}</span>
      </StyledKeySpan>
    );
  });

  return <Fragment key={shortcut}>{intersperse<ReactNode>(translatedKeys, '')}</Fragment>;
}
